import type { ReactNode } from 'react'
import { FunnelX, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { FilterDropdown } from '@/components/ui/FilterDropdown'
import { dataSourceState } from '@/lib/deep-dive-adapters'
import type { FilterGroup } from '@/lib/system-intelligence'

interface SystemFilterBarProps {
  groups: FilterGroup[]
  /** Group id → chosen option. */
  value: Record<string, string>
  onChange: (groupId: string, option: string) => void
  onClear: () => void
  /** False when every group is on "All", which disables the clear action. */
  dirty: boolean
  /** The catalogue is still being fetched — the row has nothing to draw yet. */
  isLoading?: boolean
  /** The catalogue call failed; the page still works, it just can't be scoped. */
  error?: unknown
  /**
   * The period control, rendered at the end of the same row.
   *
   * It is passed in rather than built here because the period is page state and
   * the scope chips are this component's, but it belongs on this row: both
   * answer "what am I looking at", and a separate band for one control cost a
   * whole row on the pages tuned to fit a viewport.
   */
  trailing?: ReactNode
  /** Tighter row, for a page tuned to fit the viewport. */
  compact?: boolean
}

/**
 * The drill-down's scope row: one dropdown per dimension, plus a clear action.
 *
 * The dimensions and their values come from `sales/filters` rather than being
 * hardcoded, because the values have to go back to the API byte-for-byte — they
 * are matched exactly and case-sensitively upstream, and some carry trailing
 * spaces. Anything invented here would simply match nothing.
 *
 * A failed catalogue is not a failed page: the sales figures still load unscoped,
 * so the row says what is missing and gets out of the way.
 *
 * The chips themselves are `FilterDropdown`, which draws its own option panel
 * rather than deferring to the browser's — see the note there for why.
 */
export function SystemFilterBar({
  groups,
  value,
  onChange,
  onClear,
  dirty,
  isLoading,
  error,
  trailing,
  compact,
}: SystemFilterBarProps) {
  return (
    <div
      className={cn(
        'border-line flex flex-wrap items-center gap-3 border-b',
        compact ? 'pb-2' : 'pb-4',
      )}
    >
      {isLoading && (
        <span className="text-fg-subtle flex items-center gap-2 font-mono text-[11px] font-semibold tracking-[0.08em] uppercase">
          <Loader2 aria-hidden className="size-3.5 animate-spin" />
          Loading filters…
        </span>
      )}

      {!isLoading && Boolean(error) && (
        <span
          title={dataSourceState(error).detail}
          className="text-degraded font-mono text-[11px] font-semibold tracking-[0.08em] uppercase"
        >
          Filters unavailable — showing everything
        </span>
      )}

      {!isLoading &&
        !error &&
        groups.map((group: FilterGroup) => (
          <FilterDropdown
            key={group.id}
            label={group.label}
            value={value[group.id]}
            options={group.options}
            title={group.note}
            onChange={(option) => onChange(group.id, option)}
            compact={compact}
          />
        ))}

      {trailing}

      {/* Always on screen — the row's controls read as a set, and an action that
          comes and goes is one the reader has to look for. It simply goes inert
          while every group is on its default. */}
      <button
        type="button"
        onClick={onClear}
        disabled={!dirty}
        title={dirty ? 'Reset every filter' : 'No filters applied'}
        className={cn(
          'ml-auto flex items-center gap-2 rounded-lg px-2 py-1.5 font-mono text-[11px] font-semibold',
          'tracking-[0.08em] uppercase transition-colors outline-none',
          'focus-visible:ring-primary/25 focus-visible:ring-2',
          dirty
            ? 'text-primary-bright hover:bg-primary/10 hover:text-primary'
            : 'text-fg-subtle cursor-not-allowed opacity-60',
        )}
      >
        <FunnelX className="size-4" />
        Clear filters
      </button>
    </div>
  )
}
